'use client'

import * as React from 'react'
import { type ColumnDef } from '@tanstack/react-table'
import { MoreHorizontal, Trash, Pencil, Power, PowerOff, FileJson, Eye } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { authClient } from '@/lib/auth-client'
import { format } from 'date-fns'
import { EditApiKeyDialog } from '@/components/settings/api-keys/edit-api-key-dialog'
import { ViewApiKeyDetailsDialog } from '@/components/settings/api-keys/view-api-key-details-dialog'
import { DeleteApiKeyDialog } from '@/components/settings/api-keys/delete-api-key-dialog' // Import extracted component
import { ToggleEnableApiKeyDialog } from '@/components/settings/api-keys/toggle-enable-api-key-dialog' // Import extracted component

// Helper function to format the key preview
const formatKeyPreview = (prefix: string | null | undefined, id: string): string => {
	const p = prefix ? `${prefix}_` : ''
	const last4 = id.slice(-4)
	return `${p}******${last4}`
}

// Helper function to format dates or return translated "Never" / "Invalid Date"
const formatDate = (date: Date | null | undefined, t: TFunction<'translation', undefined>): string => {
	if (!date) return t('settings.apiKeys.status.never', 'Never')
	try {
		return format(new Date(date), 'PPP')
	} catch (e) {
		return t('settings.apiKeys.status.invalidDate', 'Invalid Date')
	}
}

// Type definition remains here as it's specific to the data shape used in these columns
type ApiKeyRow = {
	id: string
	name: string
	prefix: string | null
	createdAt: Date | string
	expiresAt: Date | string | null
	enabled: boolean
	metadata: Record<string, unknown> | null
}

export const columns: ColumnDef<ApiKeyRow>[] = [
	{
		accessorKey: 'name',
		header: () => {
			const { t } = useTranslation()
			return <div className='text-left'>{t('settings.apiKeys.table.name', 'Name')}</div>
		},
		cell: ({ row }) => {
			const { t } = useTranslation()
			const hasMetadata = !!row.original.metadata && Object.keys(row.original.metadata).length > 0
			return (
				<div className='flex items-center space-x-2 text-left'>
					<span className='font-regular'>{row.getValue('name')}</span>
					{hasMetadata && (
						<TooltipProvider delayDuration={100}>
							<Tooltip>
								<TooltipTrigger>
									<FileJson className='h-4 w-4 text-muted-foreground' />
								</TooltipTrigger>
								<TooltipContent>
									<p>
										{t(
											'settings.apiKeys.metadataIndicatorTooltip',
											'This key has associated metadata.'
										)}
									</p>
								</TooltipContent>
							</Tooltip>
						</TooltipProvider>
					)}
				</div>
			)
		},
	},
	{
		accessorKey: 'prefix',
		header: () => {
			const { t } = useTranslation()
			return t('settings.apiKeys.table.prefix', 'Prefix')
		},
		cell: ({ row }) => row.getValue('prefix') || '-',
	},
	{
		id: 'keyPreview',
		header: () => {
			const { t } = useTranslation()
			return t('settings.apiKeys.table.keyPreview', 'Key Preview')
		},
		cell: ({ row }) => {
			const prefix = row.original.prefix
			const id = row.original.id
			return <span className='font-mono'>{formatKeyPreview(prefix, id)}</span>
		},
	},
	{
		accessorKey: 'createdAt',
		header: () => {
			const { t } = useTranslation()
			return t('settings.apiKeys.table.createdAt', 'Created At')
		},
		cell: ({ row }) => {
			const { t } = useTranslation()
			return formatDate(row.getValue('createdAt'), t)
		},
	},
	{
		accessorKey: 'expiresAt',
		header: () => {
			const { t } = useTranslation()
			return t('settings.apiKeys.table.expiresAt', 'Expires At')
		},
		cell: ({ row }) => {
			const { t } = useTranslation()
			return formatDate(row.getValue('expiresAt'), t)
		},
	},
	{
		accessorKey: 'enabled',
		header: () => {
			const { t } = useTranslation()
			return t('settings.apiKeys.table.status', 'Status')
		},
		cell: ({ row }) => {
			const { t } = useTranslation()
			const isEnabled = row.getValue('enabled')
			return (
				<Badge variant={isEnabled ? 'default' : 'outline'}>
					{isEnabled
						? t('settings.apiKeys.status.active', 'Active')
						: t('settings.apiKeys.status.disabled', 'Disabled')}
				</Badge>
			)
		},
	},
	{
		id: 'actions',
		header: () => {
			const { t } = useTranslation()
			return <div className='text-right'>{t('common.actions', 'Actions')}</div>
		},
		cell: ({ row }) => {
			const { t } = useTranslation()
			const apiKey = row.original
			const [isViewDetailsOpen, setIsViewDetailsOpen] = React.useState(false)

			return (
				<div className='text-right'>
					<ViewApiKeyDetailsDialog
						keyId={isViewDetailsOpen ? apiKey.id : null}
						keyName={isViewDetailsOpen ? apiKey.name : null}
						isOpen={isViewDetailsOpen}
						onOpenChange={setIsViewDetailsOpen}
					/>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant='ghost' className='h-8 w-8 p-0'>
								<span className='sr-only'>{t('settings.apiKeys.actions.openMenu', 'Open menu')}</span>
								<MoreHorizontal className='h-4 w-4' />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align='end'>
							<DropdownMenuLabel>{t('common.actions', 'Actions')}</DropdownMenuLabel>
							<DropdownMenuItem onSelect={() => setIsViewDetailsOpen(true)}>
								<Eye className='mr-2 h-4 w-4' />
								<span>{t('settings.apiKeys.actions.viewDetails', 'View Details')}</span>
							</DropdownMenuItem>
							<EditApiKeyDialog apiKey={apiKey} />
							<ToggleEnableApiKeyDialog apiKey={apiKey} onToggled={() => {}} />
							<DeleteApiKeyDialog apiKeyId={apiKey.id} onDeleted={() => {}} />
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			)
		},
	},
]
